/* eslint-disable complexity */
/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited  2023.
 */

import { PasswordInput } from '@/components/blocks/PasswordInput';
import {
	registerContainerSX,
	loginContainerSX,
	loginLabelSX,
	loginIsButtonSX,
} from '@/components/content/Register/styles/container';
import { useRegistration } from '@/data/Content/Registration';
import { useLocalization } from '@/data/Localization';
import { ID } from '@/data/types/Basic';
import { REG_EX } from '@/utils/address';
import { useForm } from '@/utils/useForm';
import {
	Button,
	Divider,
	FormControl,
	FormControlLabel,
	FormGroup,
	FormHelperText,
	Grid,
	IconButton,
	Paper,
	Stack,
	Typography,
} from '@mui/material';
import { escapeRegExp } from 'lodash';
import { ChangeEvent, FC, useState } from 'react';
import { loginButtonSX } from '@/components/content/Login/styles/button';
import { SxProps, useTheme, Theme } from '@mui/material/styles';
import { useMediaQuery } from '@mui/material';
import { Linkable } from '@/components/blocks/Linkable';
import { DpOutlinedInput } from '@/components/blocks/OutlinedInput';
import { RegistrationTextFieldSX } from '@/components/content/Register/styles/RegistrationForm';
import { BpCheckbox } from '@/components/content/Register/parts/BpCheckbox';
import { addressFormFieldsSX } from '@/components/blocks/AddressForm/styles/fields';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import {
	ModalRegisterHeaderIconBackSx,
	ModalRegisterHeaderIconCloseSx,
} from '@/components/content/Cart/parts/ModalRegister/styles/main';
import { DrawerRegisterHeaderIconBackFrameSx } from '@/components/content/Cart/parts/DrawerRegister/styles/main';
import CloseIcon from '@mui/icons-material/Close';
import { useNextRouter } from '@/data/Content/_NextRouter';
import { PasswordValidations } from '@/components/content/Register/parts/PasswordValidations';
import { useRecaptchaV3Setup } from '@/data/Content/Recaptcha';
import { RegisterTitleContentRecommendation } from '@/components/content/Register/parts/TitleContentRecommendation';

const errorsFormFields = {
	errorMessages: {
		errorMsg: 'Campo obligatorio',
		rules: {
			email: 'Por favor ingresa un correo electrónico válido',
			phone1: 'El teléfono debe ser de 10 dígitos',
			logonPassword: 'Mínimo 1 letra, 1 número y 8 caracteres',
		},
	},
};
interface ErrorInter {
	email: string;
	firstName: string;
	lastName: string;
	secondLastName: string;
	phone1: string;
	logonPassword: string;
	noticeOfPrivacy: string;
	lastNameMat: string;
}
export type UserRegistrationWithoutBoolean = {
	email: string;
	phone1: string;
	firstName: string;
	lastName: string;
	logonPassword: string;
	lastNameMat: string;
};

export type UserRegistrationWithoutExtraValidation = {
	firstName: string;
	lastName: string;
	lastNameMat: string;
};
const keyNameMap: Record<keyof UserRegistrationWithoutExtraValidation, string> = {
	firstName: 'firstName',
	lastName: 'lastName',
	lastNameMat: 'lastNameMat',
};
export const Register: FC<{
	id: ID;
	withoutLogin?: boolean;
	onGoBack?: () => void;
	onClose?: () => void;
	adaptWith?: boolean;
	origin?: 'checkout' | 'account' | 'cart';
}> = ({ withoutLogin = false, onGoBack, onClose, adaptWith = false, origin }) => {
	const registerNLS = useLocalization('RegistrationLayout');
	const RouteLocal = useLocalization('Routes');
	const router = useNextRouter();
	const { recaptchaEnabled, recaptchaExecute, recaptchaLoading } = useRecaptchaV3Setup();

	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
	const { registrationSubmit, initialRegistration } = useRegistration(
		origin ? { origin } : undefined
	);
	const {
		formRef,
		values: registrationValues,
		handleInputChange: handleChange,
		handleSubmit,
		submitting,
	} = useForm(initialRegistration);
	const [errors, setErrors] = useState<ErrorInter>({
		email: '',
		firstName: '',
		lastName: '',
		secondLastName: '',
		phone1: '',
		logonPassword: '',
		noticeOfPrivacy: '',
		lastNameMat: '',
	});
	const validateFields = (): boolean => {
		let isValid = true;

		const {
			noticeOfPrivacy,
			secondLastName,
			receiveEmail,
			rememberMe,
			logonPasswordVerify,
			logonId,
			phone,
			...registrationValuesString
		} = registrationValues;

		const fieldsWithError: string[] = (
			Object.keys(registrationValuesString) as Array<keyof UserRegistrationWithoutExtraValidation>
		)
			.filter((key) => registrationValuesString[key].trim() === '')
			.map((key) => keyNameMap[key] || key);

		if (
			Object.values(registrationValuesString as UserRegistrationWithoutBoolean).some(
				(value) => value.trim() === ''
			)
		) {
			fieldsWithError.forEach((field) => {
				setErrors((prev) => ({
					...prev,
					[field]: errorsFormFields.errorMessages.errorMsg,
				}));
			});
			isValid = false;
		} else {
			fieldsWithError.forEach((field) => {
				setErrors((prev) => ({
					...prev,
					[field]: '',
				}));
			});
		}
		if (registrationValues?.email?.trim() === '') {
			setErrors((prev) => ({
				...prev,
				email: errorsFormFields.errorMessages.errorMsg,
			}));
			isValid = false;
		} else if (!REG_EX.EMAIL.test(registrationValues.email || '')) {
			setErrors((prev) => ({
				...prev,
				email: errorsFormFields.errorMessages.rules.email,
			}));
			isValid = false;
		} else {
			setErrors((prev) => ({
				...prev,
				email: '',
			}));
		}
		if (registrationValues.phone1.trim() === '') {
			setErrors((prev) => ({
				...prev,
				email: errorsFormFields.errorMessages.errorMsg,
			}));
			isValid = false;
		} else if (registrationValues.phone1.length < 10) {
			setErrors((prev) => ({
				...prev,
				email: errorsFormFields.errorMessages.rules.phone1,
			}));
			isValid = false;
		} else {
			setErrors((prev) => ({
				...prev,
				phone1: '',
			}));
		}
		if (registrationValues.logonPassword.trim() === '') {
			setErrors((prev) => ({
				...prev,
				logonPassword: errorsFormFields.errorMessages.errorMsg,
			}));
			isValid = false;
		} else if (!handleLetterNumberAndLengthInputChange(registrationValues.logonPassword)) {
			setErrors((prev) => ({
				...prev,
			}));
			isValid = false;
		} else {
			setErrors((prev) => ({
				...prev,
				logonPassword: '',
			}));
		}
		if (!registrationValues.noticeOfPrivacy) {
			setErrors((prev) => ({
				...prev,
				noticeOfPrivacy: errorsFormFields.errorMessages.errorMsg,
			}));
			isValid = false;
		} else {
			setErrors((prev) => ({
				...prev,
				noticeOfPrivacy: '',
			}));
		}
		return isValid;
	};

	const handleOnBlur = (event: React.FocusEvent<HTMLInputElement>) => {
		const { name, value } = event.target;
		validateField(name, value);
	};

	// eslint-disable-next-line complexity
	const validateField = (name: string, value: string) => {
		if (name === 'email' || name === 'phone1' || name === 'logonPassword') {
			if (value.trim() === '') {
				setErrors((prev) => ({
					...prev,
					[name]: errorsFormFields.errorMessages.errorMsg,
				}));
			}
			if (name === 'email') {
				if (!REG_EX.EMAIL.test(value)) {
					setErrors((prev) => ({
						...prev,
						[name]: errorsFormFields.errorMessages.rules[name],
					}));
				} else {
					setErrors((prev) => ({
						...prev,
						[name]: '',
					}));
				}
			}
			if (name === 'phone1') {
				if (value.length < 10) {
					setErrors((prev) => ({
						...prev,
						[name]: errorsFormFields.errorMessages.rules[name],
					}));
				} else {
					setErrors((prev) => ({
						...prev,
						[name]: '',
					}));
				}
			}
			if (name === 'logonPassword') {
				if (!handleLetterNumberAndLengthInputChange(value)) {
					setErrors((prev) => ({
						...prev,
					}));
				} else {
					setErrors((prev) => ({
						...prev,
						[name]: '',
					}));
				}
			}
			return;
		}
		if (value.trim() === '') {
			setErrors((prev) => ({
				...prev,
				[name]: 'Campo obligatorio',
			}));
			return;
		}
		setErrors((prev) => ({
			...prev,
			[name]: '',
		}));
	};

	const handleNoticeOfPrivacyChange = (event: ChangeEvent<HTMLInputElement>) => {
		const checked = event.target.checked;

		handleChange(event);

		if (checked) {
			setErrors((prev) => ({
				...prev,
				[event.target.name]: '',
			}));
		}
	};

	const handleSubmitForm = async (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();

		const isValid = validateFields();

		if (isValid) {
			handleSubmit(async (values) => {
				let x_recaptchaToken: string | undefined = undefined;

				if (recaptchaEnabled) {
					x_recaptchaToken = await recaptchaExecute('register');
				}

				await registrationSubmit({ ...values, x_recaptchaToken });
			})(event);
			onClose?.();
		} else {
			const firstErrorField = document.querySelector('.Mui-error');
			if (firstErrorField) {
				(firstErrorField as HTMLElement).focus();
			}
		}
	};

	const handleLetterInputChange =
		(handleChange: { (event: ChangeEvent<HTMLInputElement>): void; (arg0: any): void }) =>
		(e: { target: { value: any } }) => {
			const value = e.target.value;
			const regex = /^[a-zA-Z\s]*$/;

			if (regex.test(value) || value === '') {
				handleChange(e);
			}
		};

	const handleNumberInputChange =
		(handleChange: { (event: ChangeEvent<HTMLInputElement>): void; (arg0: any): void }) =>
		(e: ChangeEvent<HTMLInputElement>) => {
			const { value, name } = e.target;
			const regex = /^[0-9]*$/;
			if (name === 'phone1' && value.length > 10) return;
			if (regex.test(value) || value === '') {
				handleChange(e);
			}
			setErrors((prev) => ({ ...prev, [name]: '' }));
		};

	const handleLetterNumberAndLengthInputChange = (value: string) => {
		const hasLetter = /[a-zA-Z]/.test(value);
		const hasNumber = /[0-9]/.test(value);
		return hasLetter && hasNumber && value.length >= 8;
	};

	const passwordValidation = (password: any) => ({
		Length: password.length >= 8,
		Number: /\d{1,}/.test(password),
		Mayus: /[A-Z]{1,}/.test(password),
	});

	return (
		<Grid container spacing={0}>
			<Grid
				item
				xs={12}
				md={6}
				m="auto"
				sx={{
					maxWidth: '700px !important',
					flexBasis: isMobile ? '100% !important' : '60% !important',
					pt: { md: '60px', xs: '20px' },
					position: onGoBack && 'relative',
				}}
			>
				{onGoBack ? (
					<IconButton
						onClick={onGoBack}
						sx={{
							...DrawerRegisterHeaderIconBackFrameSx,
							position: 'absolute',
							top: !adaptWith ? '71px' : '0px',
							left: !adaptWith ? '-6px' : '-34px',
						}}
					>
						<ArrowBackIosIcon sx={ModalRegisterHeaderIconBackSx} />
					</IconButton>
				) : null}

				{onClose ? (
					<IconButton
						onClick={onClose}
						sx={{
							...ModalRegisterHeaderIconCloseSx,
							position: 'absolute',
							top: !adaptWith ? '71px' : '0px',
							right: !adaptWith ? '5px' : '-20px',
						}}
					>
						<CloseIcon />
					</IconButton>
				) : null}
				<Paper
					sx={registerContainerSX({
						isMobile: withoutLogin || adaptWith ? false : isMobile,
						onGoBack,
						adaptWith,
					})}
					component="form"
					onSubmit={handleSubmitForm}
					ref={formRef}
					noValidate
				>
					<Stack spacing={2}>
						<RegisterTitleContentRecommendation />
						<Grid container>
							<Grid item xs={12} sm={6} pr={{ xs: 0, sm: 1 }}>
								<DpOutlinedInput
									variant="outlined"
									margin="none"
									fullWidth
									required
									name="email"
									autoComplete="email"
									id="WC_UserRegistrationAddForm_FormInput_logonId_In_Register_1_1"
									label={registerNLS.Email.t()}
									autoFocus
									onBlur={handleOnBlur}
									error={!!errors.email}
									helperText={errors.email}
									value={registrationValues.email}
									sx={addressFormFieldsSX}
									onChange={handleChange}
									type="email"
									inputProps={{
										maxLength: 50,
										pattern: REG_EX.EMAIL.source,
									}}
								/>
							</Grid>
							<Grid item xs={12} sm={6} pl={{ xs: 0, sm: 1 }} mt={{ xs: 2, sm: 0 }}>
								<DpOutlinedInput
									variant="outlined"
									margin="none"
									required
									fullWidth
									name="firstName"
									id="WC_UserRegistrationAddForm_NameEntryForm_FormInput_firstName_1"
									label={registerNLS.FirstName.t()}
									onBlur={handleOnBlur}
									error={!!errors.firstName}
									helperText={errors.firstName}
									value={registrationValues.firstName}
									sx={addressFormFieldsSX}
									onChange={handleLetterInputChange(handleChange)}
									inputProps={{
										maxLength: 40,
									}}
								/>
							</Grid>
						</Grid>
						<Grid container>
							<Grid item xs={12} sm={6} pr={{ xs: 0, sm: 1 }}>
								<DpOutlinedInput
									variant="outlined"
									margin="none"
									required
									fullWidth
									name="lastName"
									id="WC_UserRegistrationAddForm_NameEntryForm_FormInput_lastName_1"
									label={registerNLS.LastName.t()}
									onBlur={handleOnBlur}
									error={!!errors.lastName}
									helperText={errors.lastName}
									sx={addressFormFieldsSX}
									value={registrationValues.lastName}
									onChange={handleLetterInputChange(handleChange)}
									inputProps={{
										maxLength: 40,
									}}
								/>
							</Grid>
							<Grid item xs={12} sm={6} pl={{ xs: 0, sm: 1 }} mt={{ xs: 2, sm: 0 }}>
								<DpOutlinedInput
									variant="outlined"
									margin="none"
									required
									fullWidth
									name="lastNameMat"
									label={registerNLS.SecondLastName.t()}
									onBlur={handleOnBlur}
									error={!!errors.lastNameMat}
									helperText={errors.lastNameMat}
									sx={addressFormFieldsSX}
									value={registrationValues.lastNameMat}
									onChange={handleLetterInputChange(handleChange)}
									inputProps={{
										maxLength: 40,
									}}
								/>
							</Grid>
						</Grid>
						<Grid container>
							<Grid item xs={12} sm={6} pr={{ xs: 0, sm: 1 }}>
								<DpOutlinedInput
									variant="outlined"
									margin="none"
									required
									fullWidth
									name="phone1"
									type="number"
									id="WC_UserRegistrationAddForm_AddressEntryForm_FormInput_phone1_1"
									label={registerNLS.Phone.t()}
									onBlur={handleOnBlur}
									error={!!errors.phone1}
									helperText={errors.phone1}
									sx={addressFormFieldsSX}
									value={registrationValues.phone1}
									onChange={handleNumberInputChange(handleChange)}
									inputProps={{
										maxLength: 10,
									}}
								/>
							</Grid>
							<Grid item xs={12} sm={6} pl={{ xs: 0, sm: 1 }} mt={{ xs: 2, sm: 0 }}>
								<PasswordInput
									sx={
										{
											...(RegistrationTextFieldSX as SxProps<Theme>),
											...(addressFormFieldsSX as SxProps<Theme>),
										} as SxProps<Theme>
									}
									required
									name="logonPassword"
									label={registerNLS.Password.t()}
									onBlur={handleOnBlur as () => void}
									error={!!errors.logonPassword}
									helperText={errors.logonPassword}
									value={registrationValues.logonPassword}
									onChange={handleChange}
									inputProps={{
										maxLength: 100,
										pattern: escapeRegExp(registrationValues.logonPassword),
									}}
								/>
								{registrationValues.logonPassword ? (
									<PasswordValidations
										validations={{
											...passwordValidation(registrationValues.logonPassword),
											isMobile,
										}}
									/>
								) : null}
							</Grid>
						</Grid>
						<Grid container>
							<Grid item xs={12}>
								<FormControlLabel
									sx={{ fontSize: 'var(--font-size-small)' }}
									control={
										<BpCheckbox
											name="receiveEmail"
											checked={registrationValues.receiveEmail}
											onChange={handleChange}
										/>
									}
									label={
										<Typography component={'span'} sx={{ fontSize: 'var(--font-size-small)' }}>
											{registerNLS.TextContent.t()}
										</Typography>
									}
								/>
							</Grid>
							<Grid item xs={12} mt={'8px'}>
								<FormControl required error={!!errors.noticeOfPrivacy}>
									<FormGroup>
										<FormControlLabel
											control={
												<BpCheckbox
													name="noticeOfPrivacy"
													checked={registrationValues.noticeOfPrivacy}
													onChange={handleNoticeOfPrivacyChange}
												/>
											}
											label={
												<Typography component="span" sx={{ fontSize: 'var(--font-size-small)' }}>
													{'He leído y consentido el'}{' '}
													<Linkable
														component="a"
														type="link"
														variant="primary"
														href="https://www.dportenis.mx/aviso-privacidad"
														target="_blank"
													>
														{'Aviso de privacidad'}
													</Linkable>
												</Typography>
											}
										/>
									</FormGroup>
									{errors.noticeOfPrivacy ? (
										<FormHelperText
											sx={{
												marginLeft: '0px !important',
												fontSize: 'var(--font-size-caption)',
											}}
										>
											{'Campo obligatorio'}
										</FormHelperText>
									) : null}
								</FormControl>
							</Grid>
						</Grid>

						<Stack spacing={2}>
							<Button
								type="submit"
								variant="contained"
								sx={loginButtonSX}
								disabled={recaptchaLoading || submitting}
								data-testid="button-register-submit"
								id="button-register-submit"
							>
								{registerNLS.RegisterComplete.t()}
							</Button>
						</Stack>
					</Stack>
				</Paper>
				{!withoutLogin ? (
					<Paper sx={loginContainerSX({ isMobile })}>
						{isMobile ? <Divider sx={{ marginBottom: '15px' }} /> : null}
						<Stack alignItems="center" spacing={2}>
							<Typography sx={loginLabelSX}>{'¿Ya tienes cuenta?'}</Typography>
							<Linkable
								href={{
									pathname: RouteLocal.Login.route.t(),
									query: router.query.flow ? { flow: router.query.flow } : {},
								}}
								type="button"
								variant="contained"
								sx={loginIsButtonSX}
							>
								{'Inicia sesión'}
							</Linkable>
						</Stack>
					</Paper>
				) : null}
			</Grid>
		</Grid>
	);
};
